/**
 * CRM account page (A7Y-4982), with its edit controls (A7Y-4983):
 * `/admin/cmo/crm/:accountId`, from `GET /admin/crm/accounts/:id`.
 *
 * The id may be the account's slug (the old `acct-` anchor) or any former id a
 * rename left behind: the API resolves an alias to its account, so a link built
 * from an old anchor still lands here. The page shows the account's fields, its
 * next action in full, contacts, open and closed roster items, links to its
 * notes in a7y-ai-dev and to the A7Y tickets its gates name, the section notes
 * the importer kept (rendered as markdown), and its dated history newest first,
 * filterable by source and shown a page at a time.
 *
 * The API returns the whole history in the one read; the page renders it in
 * pages of `HISTORY_PAGE_SIZE` so a long section stays usable.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CrmAccountDetailResponse, CrmContactDoc, CrmEventDoc, CrmRosterItemDoc } from '@a7y/types';
import { Badge } from '@a7y/tailwind-catalyst';
import { helmDisplayText } from '@a7y/platform-health/helm';
import { PageHeader } from '@/components/PageHeader';
import { HelmViewNav } from '../HelmViews';
import { useAdminAuth } from '@/contexts/useAdminAuth';
import { api } from '@/lib/api';
import {
  HISTORY_PAGE_SIZE,
  contactName,
  filterBySource,
  historyNewestFirst,
  isoDate,
  nextDateIsTriggerFallback,
  notesForDisplay,
  orderContacts,
  sourceKinds,
  splitRoster,
} from './accountView';
import { CrmLink, CrmMarkdown, InlineMarkdown, RowText } from './CrmMarkdown';
import {
  CONTROL_CLASSES,
  CloseAccountEditor,
  ContactActions,
  ContactEditor,
  DeleteEventButton,
  HistoryLineEditor,
  LINK_BUTTON,
  LastTouchEditor,
  NextActionEditor,
  RosterItemActions,
  RosterItemEditor,
  PriorityEditor,
  SegmentEditor,
  StageEditor,
  SuperAdminAccountControls,
  Toggle,
  WatchEditor,
} from './CrmEditors';
import { crmErrorMessage, isCrmNotFoundError, isCrmUnavailableError } from './crmErrors';
import { rankLabel } from './scoreboard';
import { linkTargetsIn, notesFolders, parseLink, splitAccountName, ticketUrl, ticketsIn } from './links';

function crmRetry(err: unknown, failureCount: number): boolean {
  return !isCrmUnavailableError(err) && !isCrmNotFoundError(err) && failureCount < 1;
}

export function CrmAccountPage() {
  const accountId = useParams().accountId ?? '';
  const query = useQuery({
    queryKey: ['crm', 'account', accountId],
    queryFn: () => api.getCrmAccount(accountId),
    retry: crmRetry,
  });

  let body: React.ReactNode;
  let title = 'Account';
  if (isCrmUnavailableError(query.error)) {
    body = (
      <p data-testid="crm-unavailable" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record is available on production only.
      </p>
    );
  } else if (isCrmNotFoundError(query.error)) {
    body = (
      <p data-testid="crm-account-not-found" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        No account has the id <code>{accountId}</code>, current or former.
      </p>
    );
  } else if (query.error != null) {
    body = (
      <p role="alert" data-testid="crm-error" className="px-2 text-sm text-red-600 dark:text-red-400">
        Could not load the account: {crmErrorMessage(query.error)}
      </p>
    );
  } else if (query.data === undefined) {
    body = (
      <p data-testid="crm-loading" className="px-2 text-sm text-zinc-500">
        Loading…
      </p>
    );
  } else if (!query.data.imported || query.data.account === null) {
    body = (
      <p data-testid="crm-not-imported" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record has not been imported yet
      </p>
    );
  } else {
    title = splitAccountName(query.data.account.name).name;
    body = <AccountView detail={query.data} requestedId={accountId} />;
  }

  return (
    <div className="pb-12">
      <HelmViewNav />
      <PageHeader title={title} subtitle="Account" />
      {body}
    </div>
  );
}

function Section({ title, testId, children }: { title: string; testId: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} data-testid={testId} className="px-2">
      <h2 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">{title}</h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-zinc-500">{children}</p>;
}

function AccountView({ detail, requestedId }: { detail: CrmAccountDetailResponse; requestedId: string }) {
  const account = detail.account!;
  const { isSuperAdmin } = useAdminAuth();
  const roster = splitRoster(detail.rosterItems);
  const linkTargets = [
    ...account.links.map((l) => parseLink(l).target),
    ...linkTargetsIn(account.notes),
    ...linkTargetsIn(account.nextAction),
  ];
  const folders = notesFolders(linkTargets);
  const gates = account.gates;

  return (
    // A7Y-5251: min-w-0 and overflow-wrap keep long URLs in record text and links from widening the page at 375 px.
    <div className="min-w-0 space-y-8 [overflow-wrap:anywhere]">
      <section aria-label="Account" data-testid="crm-account-header" className="space-y-3 px-2">
        {requestedId !== account.id ? (
          <p data-testid="crm-alias-note" className="text-xs text-zinc-500">
            Opened by its former id <code>{requestedId}</code>; the account is now <code>{account.id}</code>.
          </p>
        ) : null}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-3 lg:grid-cols-6">
          <Field label="Stage" testId="crm-field-stage">
            <span className="capitalize">{account.stage}</span>
            {account.stageNote ? (
              <span className="block text-xs text-zinc-500">
                <InlineMarkdown text={account.stageNote} />
              </span>
            ) : null}
            {account.closedReason ? (
              <span className="block text-xs text-zinc-500">Closed: {account.closedReason}</span>
            ) : null}
          </Field>
          <Field label="Segment" testId="crm-field-segment">
            <span className="capitalize">{account.segment}</span>
          </Field>
          <Field label="Rank" testId="crm-field-rank">
            {rankLabel(account.priority)}
          </Field>
          <Field label="Last touch" testId="crm-field-last-touch">
            {account.lastTouch ?? '—'}
            {account.lastTouchNote ? (
              <span className="block text-xs text-zinc-500">
                <InlineMarkdown text={account.lastTouchNote} />
              </span>
            ) : null}
          </Field>
          <Field label="Next date" testId="crm-field-next-date">
            {/* A7Y-5873: a past next date reads "was due", as the Board's does. */}
            {detail.nextDate === null
              ? '—'
              : detail.nextDate < isoDate(new Date())
                ? `was due ${detail.nextDate}`
                : detail.nextDate}
            <span className="block text-xs text-zinc-500" data-testid="crm-next-date-source">
              {nextDateIsTriggerFallback(detail.rosterItems, detail.nextDate)
                ? "a trigger's fallback date"
                : 'from the open roster items'}
            </span>
          </Field>
          <Field label="Watch" testId="crm-field-watch">
            {account.watch ? <InlineMarkdown text={account.watch} /> : '—'}
          </Field>
          <Field label="Status" testId="crm-field-status">
            {detail.stale ? (
              <Badge color="amber" data-testid="crm-stale-badge">
                Stale
              </Badge>
            ) : (
              'Active'
            )}
          </Field>
        </dl>
        <div className="flex flex-wrap items-start gap-4" data-testid="crm-account-edit">
          <Toggle label="Change stage">{(close) => <StageEditor account={account} onClose={close} />}</Toggle>
          {account.stage !== 'closed' ? (
            <Toggle label="Close account">
              {(close) => <CloseAccountEditor account={account} onClose={close} />}
            </Toggle>
          ) : null}
          <Toggle label="Change segment">{(close) => <SegmentEditor account={account} onClose={close} />}</Toggle>
          <Toggle label="Change rank">{(close) => <PriorityEditor account={account} onClose={close} />}</Toggle>
          <Toggle label="Set last touch">{(close) => <LastTouchEditor account={account} onClose={close} />}</Toggle>
          <Toggle label="Set watch">{(close) => <WatchEditor account={account} onClose={close} />}</Toggle>
        </div>
      </section>

      <Section title="Next action" testId="crm-next-action">
        {/* Line breaks kept, marks and links rendered (A7Y-5342). */}
        <p className="whitespace-pre-wrap break-words text-sm text-zinc-900 dark:text-zinc-100">
          {account.nextAction ? <InlineMarkdown text={account.nextAction} /> : '—'}
        </p>
        <div className="mt-2">
          <Toggle label="Edit next action">
            {(close) => <NextActionEditor account={account} onClose={close} />}
          </Toggle>
        </div>
      </Section>

      <Contacts accountId={account.id} contacts={detail.contacts} isSuperAdmin={isSuperAdmin} />

      <Roster accountId={account.id} open={roster.open} closed={roster.closed} isSuperAdmin={isSuperAdmin} />

      <Section title="Notes and links" testId="crm-links">
        {gates.length === 0 && folders.length === 0 && account.links.length === 0 ? (
          <Empty>No gates, notes folders or links.</Empty>
        ) : (
          <div className="space-y-3 text-sm text-zinc-900 dark:text-zinc-100">
            {gates.length > 0 ? (
              <div>
                <p className="text-xs text-zinc-500">Gates</p>
                <ul className="list-disc space-y-1 pl-5" data-testid="crm-gates">
                  {gates.map((gate) => (
                    <li key={gate}>
                      {gate}
                      {ticketsIn(gate).map((t) => (
                        <a
                          key={t}
                          href={ticketUrl(t)}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="crm-gate-ticket"
                          className="ml-2 text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {t}
                        </a>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {folders.length > 0 ? (
              <div>
                <p className="text-xs text-zinc-500">Notes in a7y-ai-dev</p>
                <ul className="space-y-1" data-testid="crm-notes-folders">
                  {folders.map((f) => (
                    <li key={f.path}>
                      <a href={f.href} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline dark:text-blue-400">
                        {f.path}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {account.links.length > 0 ? (
              <div>
                <p className="text-xs text-zinc-500">Links</p>
                <ul className="space-y-1" data-testid="crm-account-links">
                  {account.links.map((raw) => {
                    const link = parseLink(raw);
                    return (
                      <li key={raw}>
                        <CrmLink target={link.target}>{link.label}</CrmLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </Section>

      {account.notes ? (
        <Section title="Section notes" testId="crm-notes">
          {/* A7Y-5873: the imported "Next date (file):" line is dropped — the page shows one next date. */}
          <CrmMarkdown markdown={notesForDisplay(account.notes)} />
        </Section>
      ) : null}

      <History accountId={account.id} events={detail.events} isSuperAdmin={isSuperAdmin} />

      {isSuperAdmin ? (
        <Section title="Super admin" testId="crm-superadmin">
          <SuperAdminAccountControls account={account} />
        </Section>
      ) : null}
    </div>
  );
}

function Field({ label, testId, children }: { label: string; testId: string; children: React.ReactNode }) {
  return (
    <div data-testid={testId}>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className="text-zinc-900 dark:text-zinc-100">{children}</dd>
    </div>
  );
}

function Contacts({
  accountId,
  contacts,
  isSuperAdmin,
}: {
  accountId: string;
  contacts: CrmContactDoc[];
  isSuperAdmin: boolean;
}) {
  const ordered = orderContacts(contacts);
  return (
    <Section title="Contacts" testId="crm-contacts">
      {ordered.length === 0 ? (
        <Empty>No contacts.</Empty>
      ) : (
        <ul className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800">
          {ordered.map((c) => {
            const manager = contactName(c.reportsTo, contacts);
            return (
              <li key={c.id} data-testid="crm-contact" className="space-y-1 py-2">
                <div className="text-zinc-900 dark:text-zinc-100">
                  {c.departed ? (
                    <span className="text-zinc-500 line-through" title="Departed">
                      {c.name}
                      <span className="sr-only"> (departed)</span>
                    </span>
                  ) : (
                    <span className="font-medium">{c.name}</span>
                  )}
                  {c.primary ? (
                    <Badge color="blue" className="ml-2" data-testid="crm-primary-badge">
                      Primary
                    </Badge>
                  ) : null}
                  {c.role ? <span className="text-zinc-600 dark:text-zinc-400"> · {c.role}</span> : null}
                  {c.email ? (
                    <a href={`mailto:${c.email}`} className="ml-2 text-blue-600 hover:underline dark:text-blue-400">
                      {c.email}
                    </a>
                  ) : null}
                </div>
                {manager ? (
                  <p className="text-xs text-zinc-500" data-testid="crm-reports-to">
                    Reports to {manager}
                  </p>
                ) : null}
                {c.note ? (
                  <p className="text-xs text-zinc-600 dark:text-zinc-400">
                    <InlineMarkdown text={c.note} />
                  </p>
                ) : null}
                <ContactActions accountId={accountId} contact={c} contacts={contacts} isSuperAdmin={isSuperAdmin} />
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-2">
        <Toggle label="Add contact">
          {(close) => <ContactEditor accountId={accountId} contact={null} contacts={contacts} onClose={close} />}
        </Toggle>
      </div>
    </Section>
  );
}

function ItemWhere({ where }: { where: string | null }) {
  if (!where) return null;
  // A bare target (`../sales/x/note.md`, a URL) is one link; a cell holding markdown
  // (`[pipeline §](…) · [A7Y-4947](…)`) or prose is rendered link by link (A7Y-5342).
  const bare = !/\]\(/.test(where) && !/\s/.test(where.trim());
  if (bare) {
    const link = parseLink(where);
    return (
      <span className="ml-2 text-xs" data-testid="crm-roster-where">
        <CrmLink target={link.target}>{link.label}</CrmLink>
      </span>
    );
  }
  return (
    <span className="ml-2 text-xs text-zinc-600 dark:text-zinc-400" data-testid="crm-roster-where">
      <InlineMarkdown text={where} />
    </span>
  );
}

export function RosterLine({ item }: { item: CrmRosterItemDoc }) {
  return (
    <span className="text-zinc-900 dark:text-zinc-100">
      <span className="text-xs uppercase tracking-wide text-zinc-500">{item.kind}</span>{' '}
      <Badge color="zinc" className="mr-1">
        {item.block}
      </Badge>
      <RowText
        markdown={item.title}
        decision={item.kind === 'decision' || item.kind === 'trigger'}
        testId="crm-roster-text"
      />
      {item.proposal ? (
        <span className="ml-1 italic text-zinc-500" data-testid="crm-roster-proposal">
          — Proposal, not a fact: <InlineMarkdown text={item.proposal} />
        </span>
      ) : null}
      <ItemWhere where={item.where} />
    </span>
  );
}

function Roster({
  accountId,
  open,
  closed,
  isSuperAdmin,
}: {
  accountId: string;
  open: CrmRosterItemDoc[];
  closed: CrmRosterItemDoc[];
  isSuperAdmin: boolean;
}) {
  return (
    <Section title="Roster" testId="crm-roster">
      <h3 className="mb-1 text-xs font-medium text-zinc-500">Open items and scheduled decisions</h3>
      {open.length === 0 ? (
        <Empty>Nothing open.</Empty>
      ) : (
        <ul className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800" data-testid="crm-roster-open">
          {open.map((item) => (
            <li key={item.id} data-testid="crm-roster-item" className="space-y-1 py-2">
              <div>
                <span className="mr-2 tabular-nums font-medium">{item.due}</span>
                <RosterLine item={item} />
              </div>
              <RosterItemActions item={item} isSuperAdmin={isSuperAdmin} />
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2">
        <Toggle label="Add roster item">
          {(close) => <RosterItemEditor accountId={accountId} onClose={close} />}
        </Toggle>
      </div>
      <h3 className="mb-1 mt-4 text-xs font-medium text-zinc-500">Closed</h3>
      {closed.length === 0 ? (
        <Empty>Nothing closed.</Empty>
      ) : (
        <ul className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800" data-testid="crm-roster-closed">
          {closed.map((item) => (
            <li key={item.id} data-testid="crm-roster-closed-item" className="py-2">
              <span className="mr-2 tabular-nums text-zinc-500">{item.closedOn ?? item.due}</span>
              <RosterLine item={item} />
              <span className="block text-xs text-zinc-600 dark:text-zinc-400">
                {item.route ? `${item.route}: ` : ''}
                {item.outcome ?? 'no outcome recorded'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function History({
  accountId,
  events,
  isSuperAdmin,
}: {
  accountId: string;
  events: CrmEventDoc[];
  isSuperAdmin: boolean;
}) {
  const [source, setSource] = useState<string | null>(null);
  const [shown, setShown] = useState(HISTORY_PAGE_SIZE);
  const kinds = sourceKinds(events);
  const lines = filterBySource(historyNewestFirst(events), source);
  const visible = lines.slice(0, shown);
  const remaining = lines.length - visible.length;

  return (
    <Section title="History" testId="crm-history">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex min-w-0 max-w-full flex-col gap-1 text-xs text-zinc-500 dark:text-zinc-400">
          Source
          {/* A7Y-5251: a long source label must not size the select past the phone viewport. */}
          <select
            value={source ?? ''}
            onChange={(e) => {
              setSource(e.target.value === '' ? null : e.target.value);
              setShown(HISTORY_PAGE_SIZE);
            }}
            className={`${CONTROL_CLASSES} max-w-full`}
          >
            <option value="">All sources</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {helmDisplayText(k)}
              </option>
            ))}
          </select>
        </label>
        <span className="pb-2 text-xs text-zinc-500" data-testid="crm-history-count">
          {visible.length} of {lines.length} lines
        </span>
      </div>
      <div className="mb-3">
        <Toggle label="Add history line">
          {(close) => <HistoryLineEditor accountId={accountId} sources={kinds} onClose={close} />}
        </Toggle>
      </div>
      {lines.length === 0 ? (
        <Empty>No history.</Empty>
      ) : (
        <ol className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800">
          {visible.map((e) => (
            <li key={e.id} data-testid="crm-history-line" className="py-2">
              <div className="text-xs text-zinc-500">
                <span className="tabular-nums font-medium text-zinc-700 dark:text-zinc-300">{e.date}</span>
                {' · '}
                {/* A7Y-5873: "BD meeting — [note](bd-sync/…)" renders its link, never the markup. */}
                <InlineMarkdown text={e.source} />
                {e.channel ? ` · ${e.channel}` : ''}
                {e.author ? ` · ${e.author}` : ''}
              </div>
              <p className="whitespace-pre-wrap break-words text-zinc-900 dark:text-zinc-100">
                <InlineMarkdown text={e.text} />
              </p>
              {e.links.length > 0 ? (
                <div className="flex flex-wrap gap-3 text-xs">
                  {e.links.map((raw) => {
                    const link = parseLink(raw);
                    return (
                      <CrmLink key={raw} target={link.target}>
                        {link.label}
                      </CrmLink>
                    );
                  })}
                </div>
              ) : null}
              {isSuperAdmin ? <DeleteEventButton eventId={e.id} /> : null}
            </li>
          ))}
        </ol>
      )}
      {remaining > 0 ? (
        <button
          type="button"
          className={`${LINK_BUTTON} mt-2`}
          onClick={() => setShown(shown + HISTORY_PAGE_SIZE)}
        >
          Show {Math.min(remaining, HISTORY_PAGE_SIZE)} older ({remaining} left)
        </button>
      ) : null}
    </Section>
  );
}
